import { type ReactNode } from 'react'
import Toggle from './Toggle'

/**
 * Arquetipo "Ajustes" — grupos con cabecera y filas etiqueta/descripción/control.
 *
 * Lo usan Configuración (modos de escritura, kill switches, ambiente) y
 * WhatsApp (canal, ventana de 24 h, plantillas de Meta). La descripción de cada
 * fila NO es decorativa: estos toggles cambian lo que el bot hace con clientes
 * reales, y el nombre de la variable solo no alcanza para decidir.
 */

export interface SettingRow {
  key: string
  label: string
  description?: string
  /** Toggle: se pasa el estado y el handler. */
  toggle?: { checked: boolean; onChange: (value: boolean) => void; disabled?: boolean }
  /** Valor de solo lectura, en pill mono. */
  value?: string
  /** Control propio (select, botón) cuando ni toggle ni value alcanzan. */
  control?: ReactNode
}

export interface SettingGroup {
  title: string
  description?: string
  rows: SettingRow[]
}

export default function SettingsList({ groups }: { groups: SettingGroup[] }) {
  return (
    <div className="flex flex-col gap-4">
      {groups.map((group) => (
        <div key={group.title} className="card overflow-hidden">
          <div className="border-b border-surface-border px-[18px] py-4">
            <h2 className="text-card font-extrabold text-ink">{group.title}</h2>
            {group.description && (
              <p className="mt-0.5 text-micro text-ink-muted">{group.description}</p>
            )}
          </div>
          <div>
            {group.rows.map((row) => (
              <div
                key={row.key}
                className="flex items-center justify-between gap-6 border-b border-surface-subtle px-[18px] py-[13px] last:border-b-0"
              >
                <div className="min-w-0">
                  <p className="text-body font-semibold text-ink">{row.label}</p>
                  {row.description && (
                    <p className="mt-px text-micro text-ink-faint">{row.description}</p>
                  )}
                </div>
                <div className="shrink-0">
                  {row.toggle ? (
                    <Toggle
                      checked={row.toggle.checked}
                      onChange={row.toggle.onChange}
                      disabled={row.toggle.disabled}
                      label={row.label}
                    />
                  ) : row.value !== undefined ? (
                    <span className="rounded-chip bg-surface-subtle px-2.5 py-1 font-mono text-micro text-ink-muted">
                      {row.value}
                    </span>
                  ) : (
                    row.control
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
